import { useCallback, useEffect, useRef } from 'react';
import { useStore } from './useStore';
import { useNotifications } from './useNotifications';
import { apiService } from '../services/api';
import { validateVideoFile, getVideoMetadata } from '../utils/helpers';
import { UploadProgress } from '../types';

export function useVideoProcessing() {
  const {
    file,
    status,
    progress,
    jobId,
    result,
    config,
    setFile,
    setVideoMetadata,
    setStatus,
    setProgress,
    setJobId,
    setResult,
    setConfig,
    reset
  } = useStore();

  const { success, error, info } = useNotifications();
  const stopPollingRef = useRef<(() => void) | null>(null);

  // Handle file selection
  const selectFile = useCallback(async (selectedFile: File) => {
    // Validate file
    const validation = validateVideoFile(selectedFile);
    if (!validation.valid) {
      error('Invalid File', validation.error || 'Please select a valid video file');
      return false;
    }

    // Get video metadata (only for browser-playable formats)
    const playableFormats = ['.mp4', '.webm', '.mov'];
    const ext = '.' + (selectedFile.name.split('.').pop()?.toLowerCase() || '');
    
    if (playableFormats.includes(ext)) {
      try {
        const metadata = await getVideoMetadata(selectedFile);
        setVideoMetadata({
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
          ...metadata
        });
      } catch (err) {
        console.error('Failed to get video metadata:', err);
        setVideoMetadata({
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
          duration: 0,
          width: 0,
          height: 0
        });
      }
    } else {
      // AVI, MKV, etc. - browser can't extract metadata
      setVideoMetadata({
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type || `video/${ext.replace('.', '')}`,
        duration: 0,
        width: 0,
        height: 0
      });
    }

    setFile(selectedFile);
    info('File Selected', `${selectedFile.name} ready for processing`);
    return true;
  }, [setFile, setVideoMetadata, error, info]);

  // Start processing
  const startProcessing = useCallback(async () => {
    if (!file) {
      error('No File', 'Please select a video file first');
      return;
    }

    try {
      setStatus('uploading');
      setProgress(0);

      // Upload video
      const uploadResponse = await apiService.uploadVideo(
        file,
        config,
        (progress: UploadProgress) => {
          setProgress(progress.percentage);
        }
      );

      if (!uploadResponse.success || !uploadResponse.data) {
        throw new Error(uploadResponse.error || 'Upload failed');
      }

      const { job_id } = uploadResponse.data;
      setJobId(job_id);
      setStatus('processing');
      setProgress(0);
      info('Processing Started', 'Your video is being analyzed...');

      // Start polling for status
      stopPollingRef.current = apiService.pollStatus(job_id, async (jobStatus) => {
        setProgress(jobStatus.progress);

        if (jobStatus.status === 'completed') {
          if (jobStatus.result) {
            setResult(jobStatus.result);
            setStatus('completed');
            success('Processing Complete', `Detected ${jobStatus.result.stats.total_vehicles} vehicles`);
          } else {
            // Fetch full result
            const resultResponse = await apiService.getResult(job_id);
            if (resultResponse.success && resultResponse.data) {
              setResult(resultResponse.data);
              setStatus('completed');
              success('Processing Complete', `Detected ${resultResponse.data.stats.total_vehicles} vehicles`);
            }
          }
        } else if (jobStatus.status === 'error') {
          setStatus('error');
          error('Processing Failed', jobStatus.error || 'An error occurred during processing');
        }
      });
    } catch (err) {
      setStatus('error');
      error('Upload Failed', err instanceof Error ? err.message : 'Failed to upload video');
    }
  }, [file, config, setStatus, setProgress, setJobId, setResult, success, error, info]);

  // Cancel processing
  const cancelProcessing = useCallback(() => {
    apiService.cancelUpload();
    
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }

    setStatus('idle');
    setProgress(0);
    info('Cancelled', 'Processing has been cancelled');
  }, [setStatus, setProgress, info]);

  // Reset everything
  const resetProcessing = useCallback(() => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }
    reset();
  }, [reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stopPollingRef.current) {
        stopPollingRef.current();
      }
    };
  }, []);

  return {
    // State
    file,
    status,
    progress,
    jobId,
    result,
    config,
    
    // Actions
    selectFile,
    startProcessing,
    cancelProcessing,
    resetProcessing,
    setConfig,
    
    // Computed
    isProcessing: status === 'uploading' || status === 'processing',
    isComplete: status === 'completed',
    hasError: status === 'error',
    canProcess: file !== null && status === 'idle'
  };
}

export default useVideoProcessing;
